import React, { useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Switch,
  Typography,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGameQuizzes } from "../../../hooks/useGameQuizzes";
import ActivityIndicator from "../../ActivityIndicator";
import {
  createGameQuiz,
  getGameQuizReuploadImpact,
  updateGameQuiz,
} from "../../../services/gameQuiz/gameQuiz.service";
import { DOUBLE_QUIZ } from "../../../services/content-ingestion/content-ingestion.types";
import { uploadCourseQuiz } from "../../../pages/App/HubSubida/contentHub.service";
import type { CourseQuizUploadResult } from "../../../pages/App/HubSubida/hubTypes";
import {
  DOUBLE_QUIZ_MESSAGE,
  QUIZ_BLOCKED_MESSAGE,
  quizKindFor,
} from "../../../pages/App/HubSubida/quizRules";
import { serverMessage } from "../../../utils/serverMessage";
import {
  GameQuizEntityInput,
  GameQuizKind,
  GameQuizReuploadImpact,
  GameWithTags,
  QuizSituation,
} from "../../../services/gameQuiz/gameQuiz.types";
import {
  addGameQuiz as addGameQuizAction,
  updateGameQuiz as updateGameQuizAction,
} from "../../../store/slices/gameQuiz";
import { apiSlice } from "../../../store/api/apiSlice";
import GamesBuilder from "./GamesBuilder";
import ImportYamlModal from "./ImportYamlModal";
import QuizReuploadImpactModal from "./QuizReuploadImpactModal";
import SituationsEditor from "./SituationsEditor";
import { findSituationError, removeSituation } from "./examSituations";
import { isExamQuizType, YamlImportResult } from "./yamlImport";
import {
  findExamQuestionError,
  gamesForKind,
  QUIZ_KIND_OPTIONS,
} from "./examQuestions";
import {
  courseLabel,
  examCourseProblem,
  ImportedCourseField,
  ResolvedCourse,
} from "./quizCourseAssignment";
import CourseAssignmentSelect from "./CourseAssignmentSelect";
import { elevenLabsService } from "../../../services/elevenlabs/elevenlabs.service";
import { getGameTags } from "../../../services/gameTags/gameTags.service";
import { GameTagDefinition } from "../../../services/gameTags/gameTags.types";

interface FormValues {
  title: string;
  published: boolean;
  kind: GameQuizKind;
  // "Curso" field, shown only after a `tipo: normal` import in create mode.
  // Never part of the quiz payload.
  course?: ResolvedCourse | null;
}

const IMPACT_UNKNOWN =
  "No se pudo comprobar si el quiz tiene tareas o intentos de alumnos. Inténtalo de nuevo.";

// El rich text de opciones produce HTML "vacío" (ej. "<p><br></p>"); para
// validar contenido real hay que comparar el texto plano.
const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

const GameQuizForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id && id !== "new";
  const numericId = isEdit ? Number(id) : undefined;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // message vía App.useApp() para que el toast se renderice dentro del
  // contexto de antd v5 (la API estática no es fiable con React 19).
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const { getGameQuizById, loadingDetail } = useGameQuizzes();
  const [games, setGames] = useState<GameWithTags[]>([]);
  const [situations, setSituations] = useState<QuizSituation[]>([]);
  const [loading, setLoading] = useState(false);
  const [bloomsTags, setBloomsTags] = useState<GameTagDefinition[]>([]);
  const [pisaTags, setPisaTags] = useState<GameTagDefinition[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  // After a `tipo: normal` import (create mode) the "Curso" field shows up:
  // creating the quiz also creates its GAME_QUIZ resource and appends it to
  // the chosen course. The course itself lives only in the form field
  // (`course`); this state just says whether the field is shown and why it
  // may start empty. null = hidden, no assignment.
  const [courseField, setCourseField] = useState<{
    warning: string | null;
  } | null>(null);
  const selectedCourse = Form.useWatch("course", form);
  const isExam = Form.useWatch("kind", form) === "EXAM";
  // SCRUM-1532: whether a YAML was imported over the quiz since it was opened.
  const [yamlImported, setYamlImported] = useState(false);
  // SCRUM-1581: the impact of a YAML re-upload waiting for the admin's
  // Confirmar or Cancelar, and how to answer the save that asked.
  const [reuploadPrompt, setReuploadPrompt] = useState<{
    impact: GameQuizReuploadImpact;
    answer: (confirmed: boolean) => void;
  } | null>(null);
  // The imported file's name: a `_QUIZ_VIDEO` one is the video's quiz.
  const [importFileName, setImportFileName] = useState<string | null>(null);
  // Why the last save did not go through, shown next to the save button.
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setCourseField(null);
  }, [numericId]);

  useEffect(() => {
    setYamlImported(false);
    setSaveError(null);
  }, [numericId]);

  // Resolves with the admin's answer to the warning: true saves.
  const confirmReupload = (impact: GameQuizReuploadImpact): Promise<boolean> =>
    new Promise((resolve) => setReuploadPrompt({ impact, answer: resolve }));

  const answerReupload = (confirmed: boolean) => {
    reuploadPrompt?.answer(confirmed);
    setReuploadPrompt(null);
  };

  // Replaces the course's quiz in place (same IDs) or creates it with its
  // resource. False when nothing was saved; the reason is shown inline.
  const saveToCourse = async (
    course: ResolvedCourse,
    title: string,
    quizGames: GameWithTags[],
    published: boolean,
    kind: GameQuizKind,
  ): Promise<boolean> => {
    const isExamQuiz = kind === "EXAM";
    if (isExamQuiz) {
      const problem = await examCourseProblem(course.id, quizGames);
      if (problem) {
        setSaveError(problem);
        return false;
      }
    }
    const upload = (studentRecords: "ask" | "overwrite") =>
      uploadCourseQuiz(course.id, {
        kind: quizKindFor(importFileName),
        quiz: isExamQuiz
          ? { title, games: quizGames, kind, situations }
          : { title, games: quizGames },
        published,
        student_records: studentRecords,
      });
    let saved: CourseQuizUploadResult;
    try {
      // SCRUM-1581: a course quiz homework or student attempts reach is
      // replaced only once the admin confirms the warning.
      saved = await upload("ask");
      if (saved.result === "needs_confirmation") {
        if (!saved.impact || !(await confirmReupload(saved.impact))) {
          return false;
        }
        saved = await upload("overwrite");
      }
    } catch (error) {
      const response = (
        error as { response?: { status?: number; data?: { error?: string } } }
      ).response;
      setSaveError(
        response?.status === 409 && response.data?.error === DOUBLE_QUIZ
          ? DOUBLE_QUIZ_MESSAGE
          : serverMessage(
              error,
              "No se pudo guardar el quiz en el curso. Inténtalo de nuevo.",
            ),
      );
      return false;
    }
    if (saved.result === "blocked") {
      setSaveError(QUIZ_BLOCKED_MESSAGE);
      return false;
    }
    dispatch(
      apiSlice.util.invalidateTags([
        { type: "GameQuiz", id: "LIST" },
        { type: "Resource", id: "LIST" },
        { type: "Course", id: "LIST" },
        { type: "Course", id: course.id },
      ]),
    );
    setCourseField(null);
    form.setFieldsValue({ course: null });
    message.success(
      saved.result === "replaced"
        ? `Quiz sustituido correctamente en el curso ${courseLabel(course)}.`
        : `Quiz creado correctamente. Recurso #${saved.resourceId} asignado al curso ${courseLabel(course)}.`,
    );
    return true;
  };

  useEffect(() => {
    (async () => {
      const [blooms, pisa] = await Promise.all([
        getGameTags("BLOOMS"),
        getGameTags("PISA"),
      ]);
      setBloomsTags(blooms);
      setPisaTags(pisa);
    })();
  }, []);

  useEffect(() => {
    if (!numericId) return;
    (async () => {
      const data = await getGameQuizById(numericId);
      if (!data) return;
      form.setFieldsValue({
        title: data.title,
        published: data.published,
        kind: data.kind ?? "NORMAL",
      });
      setSituations(data.situations ?? []);
      setGames(
        ((data.games ?? []) as GameWithTags[]).map((g) => ({
          ...g,
          tagIds: (g.tags ?? []).map((t) => t.id),
          tags: undefined,
        })),
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId]);

  const handleSubmit = async (values: FormValues) => {
    const invalidIndex = games.findIndex(
      (g) => g.type === "multiple_choice" && !g.options.some((o) => o.correct),
    );
    if (invalidIndex !== -1) {
      message.error(
        `La pregunta #${invalidIndex + 1} debe tener al menos una respuesta correcta.`,
      );
      return;
    }

    const invalidHotspotIndex = games.findIndex(
      (g) =>
        g.type === "image_hotspot" &&
        (!g.image || !(g.hotspots ?? []).some((h) => h.correct)),
    );
    if (invalidHotspotIndex !== -1) {
      message.error(
        `La pregunta #${invalidHotspotIndex + 1} debe tener imagen y al menos un área correcta.`,
      );
      return;
    }

    const emptyDistractorIndex = games.findIndex(
      (g) =>
        g.type === "categorization" &&
        (g.distractors ?? []).some((d) => !stripHtml(d.text ?? "") && !d.image),
    );
    if (emptyDistractorIndex !== -1) {
      message.error(
        `La pregunta #${emptyDistractorIndex + 1} tiene un distractor sin texto ni imagen.`,
      );
      return;
    }

    const kind = values.kind ?? "NORMAL";
    const examError =
      kind === "EXAM"
        ? (findExamQuestionError(games) ??
          findSituationError(situations, games))
        : null;
    if (examError) {
      message.error(examError);
      return;
    }

    setSaveError(null);
    setLoading(true);

    // Matching ya no usa distractores; los quizzes guardados antes del cambio
    // pueden traerlos, así que se descartan al guardar.
    const sanitizedGames = gamesForKind(
      games.map((g) => {
        if (g.type !== "matching") return g;
        const { distractors: _legacy, ...rest } = g as GameWithTags & {
          distractors?: unknown;
        };
        return rest as GameWithTags;
      }),
      kind,
    );

    const payload: GameQuizEntityInput = {
      title: values.title,
      games: sanitizedGames,
      published: values.published,
      kind,
      situations: kind === "EXAM" ? situations : [],
    };

    try {
      // Manejamos el guardado directamente (en vez de la utilidad compartida)
      // para dar un feedback de éxito/error claro y confiable en este form.
      if (isEdit && numericId) {
        // SCRUM-1581: a YAML re-upload is saved on the same quiz whatever
        // it reaches, once the admin confirms the warning, if there is one.
        if (yamlImported) {
          let impact: GameQuizReuploadImpact;
          try {
            impact = await getGameQuizReuploadImpact(numericId);
          } catch {
            setSaveError(IMPACT_UNKNOWN);
            return;
          }
          if (impact.requires_confirmation && !(await confirmReupload(impact))) {
            return;
          }
        }
        const response = await updateGameQuiz(numericId, payload);

        // Los servicios capturan el error y devuelven null; un id en la
        // respuesta es la señal fiable de que el backend guardó.
        if (!response?.id) {
          message.error(
            "No se pudieron guardar los cambios. Inténtalo de nuevo.",
          );
          return;
        }

        dispatch(updateGameQuizAction({ id: numericId, data: response }));
        // La lista vive en RTK Query: sin esto la tabla mostraría datos
        // viejos al volver.
        dispatch(
          apiSlice.util.invalidateTags([{ type: "GameQuiz", id: "LIST" }]),
        );
        message.success("Cambios guardados correctamente.");
      } else if (courseField && values.course) {
        // SCRUM-1532: a course that already has a quiz gets it replaced in
        // place; the platform creates it, with its resource, otherwise.
        const saved = await saveToCourse(
          values.course,
          values.title,
          sanitizedGames,
          values.published,
          kind,
        );
        if (!saved) return;
      } else {
        const response = await createGameQuiz(payload);

        if (!response?.id) {
          message.error("No se pudo crear el quiz. Inténtalo de nuevo.");
          return;
        }

        dispatch(addGameQuizAction(response));
        dispatch(
          apiSlice.util.invalidateTags([{ type: "GameQuiz", id: "LIST" }]),
        );

        message.success("Quiz creado correctamente.");
      }

      // Damos un instante para que la notificación de éxito sea visible
      // antes de volver a la lista.
      setTimeout(() => navigate(-1), 800);
    } catch {
      message.error(
        "Ocurrió un error inesperado al guardar. Inténtalo de nuevo.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleYamlImport = (
    result: YamlImportResult,
    imported: ImportedCourseField | null,
    fileName: string | null,
  ) => {
    setYamlImported(true);
    setImportFileName(fileName);
    setSaveError(null);
    // Every import overwrites the "Curso" field (new course, empty or
    // hidden); editing never shows it, so an existing quiz never gets a
    // duplicate resource.
    const field = isEdit ? null : imported;
    // Reset first so a validation error from a previous import goes away.
    form.resetFields(["course"]);
    form.setFieldsValue({
      title: result.title,
      published: false,
      kind: isExamQuizType(result.tipo) ? "EXAM" : "NORMAL",
      course: field?.course ?? null,
    });
    setCourseField(field ? { warning: field.warning } : null);
    setSituations(result.situations ?? []);
    setGames(result.games);
    const courseNote = !field
      ? ""
      : field.course
        ? ` Al crear el quiz se agregará como recurso al curso ${courseLabel(field.course)} (se puede cambiar en el campo Curso).`
        : " Elegí el curso en el campo Curso antes de guardar.";
    message.success(
      `Se importaron ${result.games.length} pregunta${result.games.length === 1 ? "" : "s"} desde el YAML.${courseNote} Revisá y guardá.`,
    );
  };

  if (loadingDetail) return <ActivityIndicator />;

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ published: true, kind: "NORMAL" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card
            title={isEdit ? "Editar quiz Emmi" : "Nuevo quiz Emmi"}
            extra={
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportOpen(true)}
              >
                Importar YAML
              </Button>
            }
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Título"
                  name="title"
                  rules={[
                    { required: true, message: "El título es obligatorio" },
                    { min: 3, max: 200 },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item label="Tipo de quiz" name="kind">
                  <Radio.Group
                    optionType="button"
                    options={QUIZ_KIND_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Publicado"
                  name="published"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              {courseField && (
                <Col xs={24}>
                  <Form.Item
                    label="Curso"
                    name="course"
                    rules={[
                      {
                        required: true,
                        type: "object",
                        message: "Elegí el curso al que se agrega el quiz.",
                      },
                    ]}
                    extra={
                      <Space direction="vertical" size={0}>
                        {courseField.warning && !selectedCourse && (
                          <Typography.Text type="warning">
                            {courseField.warning}
                          </Typography.Text>
                        )}
                        <span>
                          {isExam
                            ? "Al guardar, el examen se agrega como recurso a esta evaluación trimestral."
                            : "Al guardar, el quiz se agrega como recurso a este curso."}
                        </span>
                      </Space>
                    }
                  >
                    <CourseAssignmentSelect />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Card>

          {isExam && (
            <Card title={`Situaciones (${situations.length})`}>
              <SituationsEditor
                situations={situations}
                onChange={setSituations}
                onRemove={(situationId) => {
                  const next = removeSituation(situations, games, situationId);
                  setSituations(next.situations);
                  setGames(next.games);
                }}
              />
            </Card>
          )}

          <Card title={`Preguntas (${games.length})`}>
            <GamesBuilder
              games={games}
              onChange={setGames}
              generateAudio={elevenLabsService.generateAndUploadAudio}
              generateAudioWithBlanks={
                elevenLabsService.generateAndUploadAudioWithBlanks
              }
              bloomsTags={bloomsTags}
              pisaTags={pisaTags}
              isExam={isExam}
              situations={situations}
            />
          </Card>
        </Space>
        <div style={{ marginTop: 16, textAlign: "right" }}>
          {saveError && (
            <Typography.Text
              type="danger"
              role="alert"
              style={{ display: "block", marginBottom: 8 }}
            >
              {saveError}
            </Typography.Text>
          )}
          <Space>
            <Button onClick={() => navigate(-1)}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? "Guardar cambios" : "Crear "}
            </Button>
          </Space>
        </div>
      </Form>
      <ImportYamlModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        tagDefs={[...bloomsTags, ...pisaTags]}
        existingGamesCount={games.length}
        assignsCourse={!isEdit}
        exam={isExam}
        onImport={handleYamlImport}
      />
      <QuizReuploadImpactModal
        impact={reuploadPrompt?.impact ?? null}
        onConfirm={() => answerReupload(true)}
        onCancel={() => answerReupload(false)}
      />
    </div>
  );
};

export default GameQuizForm;
